import MentionsChart from './MentionsChart';
import Preloader from 'components/Dashboard/Analytics/ui/AnalyticsDashboard/Preloader';
import React, { useEffect, useRef, useState } from 'react';
import ChartDataTable, { useChartDataOptionSelected } from './ChartDataTable';
import HoverText from 'components/Dashboard/Analytics/HoverText';
import useModifyAnalyticsState from 'components/Dashboard/Analytics/useModifyAnalyticsState';
import SortArray from 'components/Dashboard/utilities/SortArray';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import MentionsRightPanel from 'components/Dashboard/Analytics/MentionsRightPanel';
import { createIncludeRepostsOption, DateOptionsMapping } from 'components/Dashboard/Analytics/createAnalyticsState';
import PartyMetaData from 'components/Dashboard/Analytics/ui/AnalyticsFilters/PartyMetadata';
import CountryPicker from 'components/Dashboard/Analytics/MentionsCountryPicker';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import FiltersBar, { DateFilterButton } from 'components/Dashboard/Analytics/ui/AnalyticsFilters/FiltersBar';
import { useContext } from 'react';
import { store } from 'components/Store';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
dayjs.extend(isSameOrAfter);
import AnalyticsTitle from 'components/Dashboard/Analytics/ui/AnalyticsTitle';
import { useTitleOnSubnavCondition } from 'components/Dashboard/Analytics/ui/AnalyticsDashboard/AnalyticsSubNav';
import getAppSite from 'utils/getAppSite';
import ChartOptionsButton from 'components/Dashboard/Analytics/ui/AnalyticsFilters/ChartOptionButton';
import useCompileMentionsDataFunctions, {
  compileData,
} from 'components/Dashboard/Analytics/hooks/useCompileMentionsDataFunctions';
import { buildCellPanelText, buildTotalCellPanelText } from './ChartDataTable';

const autoScrollFunction = ({ activeSection, chartDataContainer }) => {
  if (activeSection !== null) {
    let elements = document.querySelectorAll('.chart-data-info thead th');
    let element = elements[activeSection];
    if (element) {
      let dataContainer = chartDataContainer.current;
      let { left: tableLeft, right: tableRight } = dataContainer.getBoundingClientRect();
      let { left, right, width } = element.getBoundingClientRect();
      if (tableLeft >= right - width || tableRight <= left + width) {
        let value = element.offsetLeft - 20;
        dataContainer.scrollLeft = value;
      }
    }
  }
};

const MentionsByTopic = (props) => {
  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { activePlan } = state;
  const { enableTwitter } = activePlan;
  const {
    isLoading,
    data,
    analyticsState,
    isLoadedFirstTime,
    setData,
    rawData,
    selectedCountries,
    setSelectedCountries,
    embedWidget,
    chartType,
  } = props;
  const { partySelectedCategories, includeXRepostsTweetsSection, chartDataOptionSelectedTweets, tweetsDateInterval } =
    analyticsState;
  const { chartOptionName } = useChartDataOptionSelected();

  const [activeSection, setActiveSection] = useState(null);
  const [disableSelectSection, setDisableSelectSection] = useState(false);
  const [rightPanelState, setRightPanelState] = useState({ open: false });
  const [scrollBarPosition, setScrollBarPosition] = useState(0);
  const [openOtherDropdown, setOpenOtherDropdown] = useState(false);
  const [dropdownStyles, setDropdownStyles] = useState({});

  const { modifyState } = useModifyAnalyticsState();

  const setSelectedCategories = (val) => {
    modifyState({ property: 'partySelectedCategories', newValue: val });
  };
  const compiledDataRaw = compileData(data, analyticsState).raw;
  const createDatesLabels = () => {
    return compiledDataRaw.length > 0 ? compiledDataRaw?.map((item) => item.date) : [];
  };
  const labelDates = createDatesLabels();
  const chartRef = useRef(null);
  const { chartDataKeys, compileTotalsWithFilters, createSectionValues } = useCompileMentionsDataFunctions({
    chartRef,
    data,
    analyticsState,
    rawData,
  });

  const chartSection = useRef(null);
  const chartDataContainer = useRef(null);
  const scrollBarContainer = useRef(null);
  const chartDataSection = useRef(null);

  const emptyMessage = data.labels.length === 0;
  const isMobile = useIsMobile(768);
  const { titleOnSubnavCondition } = useTitleOnSubnavCondition();

  const autoScrollTable = useRef(null);
  autoScrollTable.current = () => {
    autoScrollFunction({ activeSection, chartDataContainer });
  };

  useEffect(() => {
    if (!rightPanelState.open) {
      autoScrollTable.current();
    }
  }, [activeSection, rightPanelState]);

  useEffect(() => {
    setSelectedCategories(chartOptionName === 'Party' ? PartyMetaData.showParties : null);
  }, [chartOptionName]);

  // Country change only flags that a recompile is owed - it can't compute yet because `data`/
  // `rawData` still hold the previous countries' results at this point (the refetch is async).
  const previousSelectedCountriesRef = useRef(selectedCountries);
  const pendingCountriesRecompileRef = useRef(false);
  useEffect(() => {
    const selectedCountriesChanged = previousSelectedCountriesRef.current !== selectedCountries;
    previousSelectedCountriesRef.current = selectedCountries;
    if (selectedCountriesChanged && rightPanelState.open) {
      pendingCountriesRecompileRef.current = true;
    }
  }, [selectedCountries]);

  // Runs once the refetch triggered by the countries change has actually landed (new `data`/
  // `rawData`), so the panel text reflects the new selection instead of the one on screen when
  // the filter changed.
  useEffect(() => {
    if (!pendingCountriesRecompileRef.current) return;
    pendingCountriesRecompileRef.current = false;
    if (!rightPanelState.open) return;

    const { dataKey, date } = rightPanelState;
    const mentionsChart = location.pathname.includes('/analytics/mentions');
    const { currentView } = analyticsState;

    if (dataKey === 'totalCell') {
      const index = labelDates.indexOf(date);
      const item = compileTotalsWithFilters()[index] ?? 0;
      const noResults = item === 0;
      const sectionValues = createSectionValues({ date, dataKey, noResults });
      setRightPanelState((prev) => ({
        ...prev,
        noResults,
        text: buildTotalCellPanelText({ item, date, currentView, mentionsChart, sectionValues }),
      }));
    } else {
      const value = data.value[dataKey]?.find((point) => point.date === date)?.value ?? 0;
      const noResults = value === 0;
      const sectionValues = createSectionValues({ date, dataKey, noResults });
      setRightPanelState((prev) => ({
        ...prev,
        noResults,
        text: buildCellPanelText({
          value,
          dataKey,
          date,
          currentView,
          tweetsDateInterval,
          mentionsChart,
          chartOptionName,
          sectionValues,
        }),
      }));
    }
  }, [data, rawData]);

  //AE: In this way we passed properties to the top nav where it's necessary for the embed and download buttons. Probably the safest way to doesn't affect the ref that other parts of the component needs.
  useEffect(() => {
    dispatch({
      type: 'MODIFY_SECTION',
      parameter: 'chartState',
      value: { isLoading, chartRef, chartSection, data, emptyMessage },
    });
    return () => {
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'chartState',
        value: {},
      });
    };
  }, [isLoading, data]);

  const highlightGraphOnHover = (index, datasetIndex) => {
    if (!disableSelectSection && !isMobile) {
      let chart = chartRef.current;
      let lengths = chart?.config?.data?.datasets?.map((item) => item?.data?.length);
      let maxIndex = lengths.indexOf(Math.max(...lengths));
      let meta = chart.getDatasetMeta(datasetIndex ?? maxIndex);
      let rect = chart.canvas.getBoundingClientRect();
      let metaIndex = meta.data[index];
      if (metaIndex) {
        let point = metaIndex.getCenterPoint();
        let evt = new MouseEvent('mousemove', {
            clientX: rect.left + point.x,
            clientY: rect.top + point.y,
          }),
          node = chart.canvas;
        node.dispatchEvent(evt);
      }
    }
  };

  const changeOrderTableLabel = (label, e) => {
    const element = e.target;
    let value = JSON.parse(JSON.stringify(data.value));
    let arrayOrder = [];
    Object.keys(value).forEach((key) => {
      let info = value[key];
      let item = info.find((item) => item.date === label);
      if (item) {
        arrayOrder.push({
          name: key,
          value: item.value,
        });
      }
    });
    let orderArray = arrayOrder.sort(SortArray('value', element.dataset.order)).map((item) => item.name);
    document
      .querySelectorAll('.sort-table-item')
      .forEach((element) => (element.dataset.order = element.dataset.order === 'asc' ? 'desc' : 'asc'));
    let newValue = {};
    orderArray.forEach((item) => {
      newValue[item] = data.value[item];
    });
    setData({ ...data, value: newValue });
    setActiveSection(null);
  };

  const tweetsSection = location.pathname.includes('/analytics/tweets');
  const disabledTwitter = !enableTwitter && tweetsSection;

  return (
    <div className='d-flex w-100 h-100'>
      <div className={`h-100 flex-grow-1 rounded-top ${embedWidget ? '' : 'bg-main-white border'}`}>
        <CustomScrollbar
          ref={scrollBarContainer}
          className={'simple-scrollbar top-content-scrollbar smooth-scrolling'}
          maximalThumbYSize={100}
          onScroll={(scrollValues) => {
            setScrollBarPosition(scrollValues?.scrollTop);
          }}
        >
          <div className='pb-4' id='mentions-chart-main-container'>
            <div className='px-lg-5 px-3 position-relative inner-mentions-container'>
              {isLoading && !isLoadedFirstTime.current && <Preloader />}
              {isLoadedFirstTime.current && (
                <>
                  {isLoading && <div className='loading-background' />}
                  <div className='my-5'>
                    <div className='d-flex align-items-center justify-content-between'>
                      {!titleOnSubnavCondition && (
                        <div className='title-spacing-adjustment pr-2'>
                          <AnalyticsTitle title={'Mentions'} />
                        </div>
                      )}
                      <CustomScrollbar className='chart-buttons-scrollbar'>
                        <div className='d-lg-flex justify-content-between flex-grow-1'>
                          <div className='d-md-flex align-items-center'>
                            <ChartOptionsButton
                              analyticsState={analyticsState}
                              isLoading={isLoading}
                              scrollBarPosition={scrollBarPosition}
                              chartType={chartType}
                            />
                            {!isMobile && (
                              <div className={`ml-md-3 ${tweetsSection ? 'd-flex align-items-center' : ''}`}>
                                {tweetsSection ? (
                                  <div className='d-flex align-items-center'>
                                    <DateFilterButton
                                      analyticsState={analyticsState}
                                      modifyFiltersBar={modifyState}
                                      customLeftValue={0}
                                      titleModifier={(name) => name?.replace('Last', 'in the last')}
                                      activeDateIntervalProp={'tweetsDateInterval'}
                                      customOptionsNames={DateOptionsMapping?.filter((item) => {
                                        if (item?.name === 'Custom') return true;
                                        const firstTweetDate = import.meta.env.VITE_APP_FIRST_TWEET_DATE;
                                        if (!firstTweetDate) return true;
                                        if (!item?.filter || !item?.filter[0]) return false;
                                        return dayjs(item.filter[0]).isSameOrAfter(firstTweetDate);
                                      })?.map((item) => item?.name)}
                                      chartType={chartType}
                                    />

                                    {chartDataOptionSelectedTweets !== 'Contribution type' &&
                                      createIncludeRepostsOption({ tweetsDateInterval }) && (
                                        <button
                                          className={`general-button border specific-y-padding-7 specific-x-padding-22 mr-3 rounded-button d-flex nowrap-item ml-3`}
                                          onClick={(e) => {
                                            modifyState({
                                              property: 'includeXRepostsTweetsSection',
                                              newValue: !includeXRepostsTweetsSection,
                                            });
                                          }}
                                        >
                                          <span>Include reposts </span>
                                          <span
                                            className={`specific-left-margin-18 toggle-button-${
                                              includeXRepostsTweetsSection ? 'active' : 'inactive'
                                            }`}
                                          >
                                            <span className='toggle-button' />
                                          </span>
                                        </button>
                                      )}
                                  </div>
                                ) : (
                                  <>
                                    {getAppSite() === 'global' && (
                                      <CountryPicker
                                        selectedCountries={selectedCountries}
                                        setSelectedCountries={setSelectedCountries}
                                        scrollBarPosition={scrollBarPosition}
                                      />
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <div className='d-none d-lg-flex align-items-center pl-3'>
                            <FiltersBar
                              typeOfChartSections={true}
                              scrollBarPosition={scrollBarPosition}
                              hideDateFilter={tweetsSection}
                            />
                            {!embedWidget && <HoverText chartWord={'chart'} />}
                          </div>
                        </div>
                      </CustomScrollbar>
                    </div>
                    {isMobile && (
                      <CountryPicker
                        selectedCountries={selectedCountries}
                        setSelectedCountries={setSelectedCountries}
                      />
                    )}
                  </div>
                  <div ref={chartSection}>
                    <MentionsChart
                      key={`chart-ref-${partySelectedCategories?.length}`}
                      ref={chartRef}
                      data={data}
                      compiledData={compileTotalsWithFilters()}
                      chartOptionName={data?.chartOptionName}
                      analyticsState={analyticsState}
                      setActiveSection={setActiveSection}
                      scrollRefs={{ chartDataSection, scrollBarContainer }}
                      setDisableSelectSection={setDisableSelectSection}
                      emptyMessage={emptyMessage}
                      activeSection={activeSection}
                      isLoading={isLoading}
                      selectedCategories={partySelectedCategories}
                      setSelectedCategories={setSelectedCategories}
                      setRightPanelState={setRightPanelState}
                      rawData={rawData}
                      chartDataKeys={chartDataKeys}
                      labelDates={labelDates}
                      openDropdownState={{ openOtherDropdown, setOpenOtherDropdown, dropdownStyles, setDropdownStyles }}
                      scrollBarPosition={scrollBarPosition}
                      embedWidget={embedWidget}
                      disabledTwitter={disabledTwitter}
                    />
                  </div>
                  {!emptyMessage && !embedWidget && !disabledTwitter && (
                    <div ref={chartDataSection}>
                      <ChartDataTable
                        analyticsState={analyticsState}
                        chartDataKeys={chartDataKeys}
                        chartDataContainer={chartDataContainer}
                        activeSection={activeSection}
                        highlightGraphOnHover={highlightGraphOnHover}
                        labelDates={labelDates}
                        compiledData={compileTotalsWithFilters()}
                        data={data}
                        changeOrderTableLabel={changeOrderTableLabel}
                        rawData={rawData}
                        rightPanelState={rightPanelState}
                        setRightPanelState={setRightPanelState}
                        createSectionValues={createSectionValues}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </CustomScrollbar>
      </div>
      {rightPanelState.open && (
        <MentionsRightPanel
          contentState={rightPanelState}
          setRightPanelState={setRightPanelState}
          analyticsState={analyticsState}
          createSectionValues={createSectionValues}
        />
      )}
    </div>
  );
};

export { autoScrollFunction };
export default MentionsByTopic;
